import { clockWall, compareClocks } from './hlc.js';
import {
  CONTENT_FIELDS,
  MERGE_FIELDS,
  type Card,
  type CardFields,
  type CardPatch,
  type Clock,
  type Conflict,
  type ContentField,
  type MergeField,
  type SettingPatch,
  type SettingRow,
} from './types.js';

export type MergeStatus = 'created' | 'applied' | 'merged' | 'unchanged' | 'stale';

export interface MergeResult {
  card: Card;
  status: MergeStatus;
  applied: MergeField[];
  rejected: MergeField[];
  conflicts: Conflict[];
}

export class IncompleteCardError extends Error {
  constructor(public readonly missing: string[]) {
    super(`new card is missing: ${missing.join(', ')}`);
    this.name = 'IncompleteCardError';
  }
}

const REQUIRED_ON_CREATE: readonly MergeField[] = ['title', 'motivation', 'band', 'rank', 'energy', 'status'];
const CONTENT_SET: ReadonlySet<string> = new Set(CONTENT_FIELDS);

const DEFAULT_FIELDS: CardFields = {
  title: '',
  notes: null,
  firstStep: null,
  energy: 2,
  dueAt: null,
  reminders: [],
  repeat: null,
  dread: null,
  status: 'open',
  deletedAt: null,
  motivation: 0,
  band: 1,
  rank: 0,
  doneAt: null,
  touchedAt: null,
  attempts: [],
  doneLog: [],
  inbox: false,
};

export function isContentField(field: MergeField): field is ContentField {
  return CONTENT_SET.has(field);
}

export function isMergeField(name: string): name is MergeField {
  return (MERGE_FIELDS as readonly string[]).includes(name);
}

export function conflictId(cardId: string, field: MergeField, losingClock: Clock): string {
  return `${cardId}:${field}:${losingClock}`;
}

/** Structural: a reminder list written identically on two devices is not a conflict. */
export function valuesEqual(a: unknown, b: unknown): boolean {
  const x = a ?? null;
  const y = b ?? null;
  if (x === y) return true;
  if (typeof x !== 'object' || typeof y !== 'object' || x === null || y === null) return false;
  return JSON.stringify(x) === JSON.stringify(y);
}

export function pickFields(card: Card): CardFields {
  const out: Record<string, unknown> = {};
  for (const f of MERGE_FIELDS) out[f] = card[f];
  return out as unknown as CardFields;
}

function conflict(cardId: string, field: ContentField, losingValue: unknown, losingClock: Clock, now: number): Conflict {
  return { id: conflictId(cardId, field, losingClock), cardId, field, losingValue, losingClock, deviceName: null, createdAt: now };
}

/** A card the server has never seen: every provided field is taken as is, bases are irrelevant. */
export function createCardFromPatch(patch: CardPatch, now: number): MergeResult {
  const missing: string[] = [];
  if (!patch.boardId) missing.push('boardId');
  for (const f of REQUIRED_ON_CREATE) if (!patch.fields[f]) missing.push(f);
  if (missing.length) throw new IncompleteCardError(missing);

  const values: Record<string, unknown> = { ...DEFAULT_FIELDS };
  const clocks: Card['clocks'] = {};
  const applied: MergeField[] = [];
  let updatedAt = 0;
  for (const f of MERGE_FIELDS) {
    const fp = patch.fields[f];
    if (!fp) continue;
    values[f] = fp.value;
    clocks[f] = fp.clock;
    updatedAt = Math.max(updatedAt, clockWall(fp.clock));
    applied.push(f);
  }
  const card: Card = {
    ...(values as unknown as CardFields),
    id: patch.id,
    boardId: patch.boardId as string,
    createdAt: patch.createdAt ?? now,
    updatedAt: updatedAt || now,
    clocks,
  };
  return { card, status: 'created', applied, rejected: [], conflicts: [] };
}

/**
 * Per-field merge of one patch into the stored card.
 *
 * A field is sequential when the patch's base equals the stored clock: the device saw the
 * current value, so its write simply applies. Otherwise two devices wrote concurrently and the
 * higher clock wins; for content fields the loser is kept as a conflict unless both values
 * are identical. Deletion follows one more rule: the latest content edit or deletion by clock
 * wins, so an edit newer than a deletion resurrects the card and a deletion older than an
 * edit is refused. Both cases leave a conflict on `deletedAt` so nothing vanishes silently.
 */
export function mergePatch(existing: Card, patch: CardPatch, now: number): MergeResult {
  const values: Record<string, unknown> = { ...pickFields(existing) };
  const clocks: Card['clocks'] = { ...existing.clocks };
  const applied: MergeField[] = [];
  const rejected: MergeField[] = [];
  const conflicts: Conflict[] = [];
  let updatedAt = existing.updatedAt;

  const newestContentClock = (except: MergeField): Clock | null => {
    let best: Clock | null = null;
    for (const f of CONTENT_FIELDS) {
      if (f === except) continue;
      const c = clocks[f];
      if (c && (!best || compareClocks(c, best) > 0)) best = c;
    }
    return best;
  };

  for (const f of MERGE_FIELDS) {
    const inc = patch.fields[f];
    if (!inc) continue;
    const cur = clocks[f] ?? null;
    if (cur !== null && inc.clock === cur) continue; // the same write, replayed

    if (f === 'deletedAt' && inc.value !== null) {
      const newerEdit = newestContentClock('deletedAt');
      if (newerEdit && compareClocks(newerEdit, inc.clock) > 0) {
        conflicts.push(conflict(patch.id, 'deletedAt', inc.value, inc.clock, now));
        rejected.push(f);
        continue;
      }
    }

    const incomingWins = cur === null || compareClocks(inc.clock, cur) > 0;
    const sequential = cur === null || (inc.base ?? null) === cur;
    if (!sequential && isContentField(f) && !valuesEqual(values[f], inc.value)) {
      conflicts.push(
        incomingWins
          ? conflict(patch.id, f, values[f], cur as Clock, now)
          : conflict(patch.id, f, inc.value, inc.clock, now),
      );
    }
    if (incomingWins) {
      values[f] = inc.value;
      clocks[f] = inc.clock;
      updatedAt = Math.max(updatedAt, clockWall(inc.clock));
      applied.push(f);
    } else {
      rejected.push(f);
    }
  }

  if (values['deletedAt'] !== null && !patch.fields.deletedAt) {
    const deletionClock = clocks.deletedAt ?? null;
    const appliedContent = applied.filter((f) => f !== 'deletedAt' && isContentField(f));
    const newest = appliedContent.reduce<Clock | null>((best, f) => {
      const c = clocks[f] as Clock;
      return !best || compareClocks(c, best) > 0 ? c : best;
    }, null);
    if (newest && (!deletionClock || compareClocks(newest, deletionClock) > 0)) {
      conflicts.push(conflict(patch.id, 'deletedAt', values['deletedAt'], deletionClock ?? newest, now));
      values['deletedAt'] = null;
      clocks.deletedAt = newest;
      applied.push('deletedAt');
    }
  }

  let status: MergeStatus;
  if (applied.length === 0) status = rejected.length ? 'stale' : 'unchanged';
  else if (conflicts.length || rejected.length) status = 'merged';
  else status = 'applied';

  const card: Card = { ...existing, ...(values as unknown as CardFields), clocks, updatedAt };
  return { card, status, applied, rejected, conflicts };
}

export function applyCardPatch(existing: Card | null, patch: CardPatch, now: number): MergeResult {
  return existing ? mergePatch(existing, patch, now) : createCardFromPatch(patch, now);
}

/** Settings are single values per key: newest clock wins, silently. */
export function mergeSetting(
  existing: SettingRow | null,
  incoming: SettingPatch,
): { row: SettingRow; status: 'applied' | 'unchanged' | 'stale' } {
  if (!existing) return { row: { json: incoming.json, clock: incoming.clock }, status: 'applied' };
  const cmp = compareClocks(incoming.clock, existing.clock);
  if (cmp === 0) return { row: existing, status: 'unchanged' };
  if (cmp < 0) return { row: existing, status: 'stale' };
  return { row: { json: incoming.json, clock: incoming.clock }, status: 'applied' };
}
